/**
 * API Service für Protected Bricks Backend
 *
 * Dieser Service kapselt alle API-Aufrufe an das Backend und verwendet
 * die generierten API-Klassen aus dem OpenAPI-Generator.
 */

import { decodeJwt } from '../utils/jwtHelper';
import {
  Configuration,
  AccountApi,
  ManufacturersApi,
  SetsApi,
  DocumentsApi,
  ProtectedBricksApi,
  Middleware,
  ResponseContext,
} from '../api/protected-bricks/generated/src';
import type {
  Document as ApiDocument,
  AccountData as ApiAccountData,
  AnalysisHistory as ApiAnalysisHistory,
  AnalyzedBrick as ApiAnalyzedBrick,
  SetWithManufacturer as ApiSetWithManufacturer,
  DocumentPageable as ApiDocumentPageable,
  SetPageable as ApiSetPageable,
  Pageable as ApiPageable,
  ProtectedBricksPageable as ApiProtectedBricksPageable,
  ProtectedBrickItem as ApiProtectedBrickItem,
} from '../api/protected-bricks/generated/src';
import { AnalyzeSetRequestAnalyzeTypeEnum } from '../api/protected-bricks/generated/src';
import type {
  Document,
  UserData,
  AnalysisHistory,
  AnalyzedBrick,
  DocumentPageable,
  SetPageable,
  SetWithManufacturer,
  Pageable,
  ProtectedBricksPageable,
  ProtectedBrickItem,
} from '../types';

// Base URL aus Environment-Variable oder Fallback
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

/**
 * Token-Provider-Funktion
 * Diese Funktion wird von außen (App.tsx) gesetzt und gibt immer den aktuellen Token zurück
 */
let tokenProvider: (() => string | null) | null = null;

/**
 * Helper: Konvertiert Date oder String zu ISO-String
 */
function toISOString(date: Date | string): string {
  if (date instanceof Date) {
    return date.toISOString();
  }
  return String(date);
}

/**
 * Konvertierungsfunktionen zwischen API-Typen (Date) und lokalen Typen (string)
 */
function convertApiAnalyzedBrickToLocal(apiBrick: ApiAnalyzedBrick): AnalyzedBrick {
  return {
    id: apiBrick.id,
    description: apiBrick.description,
    protected: apiBrick._protected,
    protectionDetails: apiBrick.protectionDetails,
  };
}

function convertApiDocumentToLocal(apiDoc: ApiDocument): Document {
  return {
    ...apiDoc,
    uploadDate: toISOString(apiDoc.uploadDate),
    bricksData: apiDoc.bricksData?.map(convertApiAnalyzedBrickToLocal),
  };
}

function convertApiAccountDataToLocal(apiData: ApiAccountData): UserData {
  return {
    ...apiData,
    resetDate: toISOString(apiData.resetDate),
    plan: (apiData.plan === 'free' || apiData.plan === 'premium') ? apiData.plan : 'free',
  };
}

function convertApiAnalysisHistoryToLocal(apiHistory: ApiAnalysisHistory): AnalysisHistory {
  return {
    ...apiHistory,
    date: toISOString(apiHistory.date),
  };
}

function convertApiSetWithManufacturerToLocal(apiSet: ApiSetWithManufacturer): SetWithManufacturer {
  return {
    ...apiSet,
    analyzedDate: toISOString(apiSet.analyzedDate),
    manufacturer: apiSet.manufacturerName,
    // Konvertiere null zu undefined für analyzeType
    analyzeType: apiSet.analyzeType ?? undefined,
  };
}

function convertApiPageableToLocal(apiPageable: ApiPageable): Pageable {
  return {
    totalItems: apiPageable.totalItems ?? 0,
    limit: apiPageable.limit ?? 10,
    hasPrevPage: apiPageable.hasPrevPage ?? false,
    hasNextPage: apiPageable.hasNextPage ?? false,
    page: apiPageable.page ?? 1,
    totalPages: apiPageable.totalPages ?? 1,
    offset: apiPageable.offset,
    prevPage: apiPageable.prevPage,
    nextPage: apiPageable.nextPage,
    pagingCounter: apiPageable.pagingCounter,
  };
}

function convertApiDocumentPageableToLocal(apiData: ApiDocumentPageable): DocumentPageable {
  return {
    items: apiData.items.map(convertApiDocumentToLocal),
    meta: convertApiPageableToLocal(apiData.meta),
  };
}

function convertApiSetPageableToLocal(apiData: ApiSetPageable): SetPageable {
  return {
    items: apiData.items.map(convertApiSetWithManufacturerToLocal),
    meta: convertApiPageableToLocal(apiData.meta),
  };
}

function convertApiProtectedBrickItemToLocal(apiItem: ApiProtectedBrickItem): ProtectedBrickItem {
  return {
    partNumber: apiItem.partNumber,
    description: apiItem.description,
    protected: apiItem._protected,
    protectionDetails: apiItem.protectionDetails,
    gmNumber: apiItem.gmNumber,
    filingDate: apiItem.filingDate ? toISOString(apiItem.filingDate) : undefined,
    registrationDate: apiItem.registrationDate ? toISOString(apiItem.registrationDate) : undefined,
    expiryDate: apiItem.expiryDate ? toISOString(apiItem.expiryDate) : undefined,
    status: apiItem.status as 'active' | 'expired',
  };
}

function convertApiProtectedBricksPageableToLocal(apiData: ApiProtectedBricksPageable): ProtectedBricksPageable {
  return {
    items: apiData.items.map(convertApiProtectedBrickItemToLocal),
    meta: convertApiPageableToLocal(apiData.meta),
  };
}

/**
 * Middleware für Fehlerbehandlung
 */
class ErrorHandlingMiddleware implements Middleware {
  async post(context: ResponseContext): Promise<Response | void> {
    const { response } = context;

    if (response.status === 401) {
      // Nicht autorisiert - Token abgelaufen oder ungültig
      console.error('Unauthorized - Token expired or invalid');
      // Hier könnte man einen Redirect zum Login machen
    } else if (response.status === 402) {
      // Kontingent erschöpft
      console.error('Quota exceeded');
    } else if (!response.ok) {
      console.error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response;
  }
}

/**
 * Custom Error für fehlenden Token
 */
export class NoTokenError extends Error {
  constructor() {
    super('Kein Authentifizierungs-Token vorhanden. Bitte erneut einloggen.');
    this.name = 'NoTokenError';
  }
}

/**
 * Prüft ob ein gültiger Token vorhanden ist
 * Wirft NoTokenError wenn kein Token gesetzt ist
 */
function requireToken(): void {
  if (!tokenProvider) {
    throw new NoTokenError();
  }
  const token = tokenProvider();
  if (!token || token.trim().length === 0) {
    throw new NoTokenError();
  }
}

/**
 * API Configuration erstellen
 */
function createApiConfiguration(): Configuration {
  const errorMiddleware = new ErrorHandlingMiddleware();

  return new Configuration({
    basePath: BASE_URL,
    accessToken: async () => {
      // Hole Token dynamisch vom tokenProvider (wenn gesetzt)
      // Dadurch wird immer der aktuelle Token verwendet, auch nach Silent Renew
      if (tokenProvider) {
        const token = tokenProvider();
        return token || '';
      }
      return '';
    },
    middleware: [errorMiddleware],
  });
}

/**
 * API Service Class
 *
 * Wrapper um die generierten API-Klassen für einfacheren Zugriff
 */
class ApiService {
  private config: Configuration;
  private accountApi: AccountApi;
  private manufacturersApi: ManufacturersApi;
  private setsApi: SetsApi;
  private documentsApi: DocumentsApi;
  private protectedBricksApi: ProtectedBricksApi;

  constructor() {
    this.config = createApiConfiguration();
    this.accountApi = new AccountApi(this.config);
    this.manufacturersApi = new ManufacturersApi(this.config);
    this.setsApi = new SetsApi(this.config);
    this.documentsApi = new DocumentsApi(this.config);
    this.protectedBricksApi = new ProtectedBricksApi(this.config);
  }

  /**
   * Gibt die API-Konfiguration zurück
   */
  getConfiguration(): Configuration {
    return this.config;
  }

  /**
   * Setzt den Token-Provider für alle API-Aufrufe
   * Der Provider ist eine Funktion, die immer den aktuellen Token zurückgibt
   * Dies ermöglicht dynamisches Token-Handling (z.B. nach Silent Renew)
   */
  setTokenProvider(provider: (() => string | null) | null) {
    tokenProvider = provider;

    // Dekodiere und logge Token für Debugging (nur wenn Provider gesetzt wird)
    if (provider) {
      const token = provider();
      if (token) {
        const decoded = decodeJwt(token);
        console.log('Token Provider gesetzt. Token-Claims:', decoded);
      }
    }

    // APIs mit neuer Config neu initialisieren
    // Die neue Config verwendet den tokenProvider
    this.config = createApiConfiguration();
    this.accountApi = new AccountApi(this.config);
    this.manufacturersApi = new ManufacturersApi(this.config);
    this.setsApi = new SetsApi(this.config);
    this.documentsApi = new DocumentsApi(this.config);
    this.protectedBricksApi = new ProtectedBricksApi(this.config);
  }

  // /**
  //  * @deprecated Verwenden Sie stattdessen setTokenProvider()
  //  * Legacy-Methode für Rückwärtskompatibilität
  //  */
  // setAccessToken(token: string | null) {
  //   console.warn('setAccessToken() ist deprecated. Verwenden Sie setTokenProvider() für besseres Token-Handling.');
  //   // Erstelle einen einfachen Provider, der den statischen Token zurückgibt
  //   this.setTokenProvider(token ? () => token : null);
  // }

  /**
   * Sets abrufen (paginiert)
   * @param page Seitennummer (1-basiert), default: 1
   * @param limit Einträge pro Seite, default: 10
   * @param manufacturer Filter nach Hersteller
   * @param status Filter nach Status (all, safe, unsafe)
   * @param search Suchbegriff
   */
  async getSetsPageable(
    page: number = 1,
    limit: number = 10,
    manufacturer?: string,
    status?: string,
    search?: string
  ): Promise<SetPageable> {
    requireToken();
    const apiData = await this.manufacturersApi.getManufacturers({
      page,
      limit,
      manufacturer,
      status: status as any,
      search,
    });
    return convertApiSetPageableToLocal(apiData);
  }

  /**
   * Sets abrufen (nicht paginiert, für Kompatibilität)
   */
  async getSets(manufacturer?: string, status?: string, search?: string): Promise<SetWithManufacturer[]> {
    requireToken();
    const apiData = await this.setsApi.getSets({ manufacturer, status: status as any, search });
    return apiData.map(convertApiSetWithManufacturerToLocal);
  }

  /**
   * Set by ID abrufen
   */
  async getSetById(id: string): Promise<SetWithManufacturer> {
    requireToken();
    const apiData = await this.setsApi.getSetById({ id });
    return convertApiSetWithManufacturerToLocal(apiData);
  }

  /**
   * Set-Analyse starten
   * @param id Set-ID
   * @param analyzeType Art der Analyse (SIMPLE, PROTECTED, ALL)
   */
  async analyzeSet(id: string, analyzeType: AnalyzeSetRequestAnalyzeTypeEnum): Promise<SetWithManufacturer> {
    requireToken();
    const apiData = await this.setsApi.analyzeSet({
      id,
      analyzeSetRequest: { analyzeType },
    });
    return convertApiSetWithManufacturerToLocal(apiData);
  }

  /**
   * Dokumente abrufen (paginiert)
   * @param page Seitennummer (1-basiert), default: 1
   * @param limit Einträge pro Seite, default: 10
   */
  async getDocuments(page: number = 1, limit: number = 10): Promise<DocumentPageable> {
    requireToken();
    const apiData = await this.documentsApi.getDocuments({ page, limit });
    return convertApiDocumentPageableToLocal(apiData);
  }

  /**
   * Dokument by ID abrufen
   */
  async getDocumentById(id: string): Promise<Document> {
    requireToken();
    const apiData = await this.documentsApi.getDocumentById({ id });
    return convertApiDocumentToLocal(apiData);
  }

  /**
   * Dokument hochladen
   * @param file Die hochzuladende Datei
   * @param isPublic Ob das Set öffentlich sichtbar sein soll (default: true)
   * @param manufacturer Hersteller des Sets (Pflichtfeld bei privaten Sets)
   * @param setName Name des Sets (Pflichtfeld bei privaten Sets)
   * @param setNumber Set-Nummer (Pflichtfeld bei privaten Sets)
   */
  async uploadDocument(
    file: File,
    isPublic: boolean = true,
    manufacturer?: string,
    setName?: string,
    setNumber?: string
  ): Promise<Document> {
    requireToken();
    const apiData = await this.documentsApi.uploadDocument({
      file,
      isPublic,
      manufacturer,
      setName,
      setNumber,
    });
    return convertApiDocumentToLocal(apiData);
  }

  /**
   * Dokument löschen
   */
  async deleteDocument(id: string): Promise<void> {
    requireToken();
    return this.documentsApi.deleteDocument({ id });
  }

  /**
   * Geschützte Steine abrufen (paginiert)
   * @param page Seitennummer (1-basiert), default: 1
   * @param limit Einträge pro Seite, default: 25
   * @param status Filter nach Status (active, expired, all)
   * @param search Suchbegriff für Teilenummer oder GM-Nummer
   */
  async getProtectedBricks(
    page: number = 1,
    limit: number = 25,
    status?: 'active' | 'expired' | 'all',
    search?: string
  ): Promise<ProtectedBricksPageable> {
    requireToken();
    const apiData = await this.protectedBricksApi.getProtectedBricks({
      page,
      limit,
      status: status as any,
      search,
    });
    return convertApiProtectedBricksPageableToLocal(apiData);
  }

  /**
   * Geschützten Stein by Part Number abrufen
   */
  async getProtectedBrickByPartNumber(partNumber: string) {
    requireToken();
    return this.protectedBricksApi.getProtectedBrickByPartNumber({ partNumber });
  }

  /**
   * Account-Daten abrufen
   */
  async getAccount(): Promise<UserData> {
    requireToken();
    const apiData = await this.accountApi.getAccount();
    return convertApiAccountDataToLocal(apiData);
  }

  /**
   * Nutzungsdaten aktualisieren
   */
  async updateAccountUsage(bricksAnalyzed: number, documentId: string): Promise<UserData> {
    requireToken();
    const apiData = await this.accountApi.updateAccountUsage({
      updateAccountUsageRequest: {
        bricksAnalyzed,
        documentId,
      },
    });
    return convertApiAccountDataToLocal(apiData);
  }

  /**
   * Analyse-Historie abrufen
   */
  async getAccountHistory(): Promise<AnalysisHistory[]> {
    requireToken();
    const apiData = await this.accountApi.getAccountHistory();
    return apiData.map(convertApiAnalysisHistoryToLocal);
  }
}

export const apiService = new ApiService();
export default apiService;
export { requireToken };